import apiClient from './api';

/**
 * Patient Service
 * Handles all patient-related API operations
 */

class PatientService {
  /**
   * Create a new patient
   * @param {Object} patientData - Patient data (name, email optional)
   * @returns {Promise} Patient creation response
   */
  async createPatient(patientData) {
    try {
      const response = await apiClient.post('/api/patients', patientData);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to create patient',
      };
    }
  }

  /**
   * Search patients
   * @param {string} searchTerm - Search term (patient ID, name, or email)
   * @param {string} patientId - Optional specific patient ID filter
   * @returns {Promise} List of patients
   */
  async searchPatients(searchTerm = '', patientId = '') {
    try {
      const params = {};
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (patientId) {
        params.patientId = patientId;
      }

      const response = await apiClient.get('/api/patients/search', { params });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to search patients',
      };
    }
  }

  /**
   * Get patient by ID
   * @param {string} patientId - Patient MongoDB ID
   * @returns {Promise} Patient details
   */
  async getPatientById(patientId) {
    try {
      const response = await apiClient.get(`/api/patients/${patientId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch patient',
      };
    }
  }

  /**
   * Get patient by patientId (PT0001 format)
   * @param {string} patientId - Patient ID in PT0001 format
   * @returns {Promise} Patient details
   */
  async getPatientByPatientId(patientId) {
    try {
      const response = await apiClient.get(`/api/patients/patient-id/${patientId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch patient',
      };
    }
  }

  /**
   * Update patient
   * @param {string} patientId - Patient MongoDB ID
   * @param {Object} updateData - Updated patient data
   * @returns {Promise} Update response
   */
  async updatePatient(patientId, updateData) {
    try {
      const response = await apiClient.put(`/api/patients/${patientId}`, updateData);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to update patient',
      };
    }
  }

  /**
   * Delete patient
   * @param {string} patientId - Patient MongoDB ID
   * @returns {Promise} Deletion response
   */
  async deletePatient(patientId) {
    try {
      const response = await apiClient.delete(`/api/patients/${patientId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to delete patient',
      };
    }
  }

  /**
   * Get patients with their latest scan (optimized for dashboard)
   * @param {Object} filters - Filter options (page, limit, search, status, risk, sortField, sortDirection)
   * @returns {Promise} Patients with latest scan data
   */
  async getPatientsWithLatestScan(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.risk) params.append('risk', filters.risk);
      if (filters.sortField) params.append('sortField', filters.sortField);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

      const queryString = params.toString();
      const url = `/api/patients/with-latest-scan${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient.get(url);
      const data = response.data.data;
      return {
        success: true,
        data: data.patients,
        total: data.total,
        totalPages: data.pages,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch patients with latest scan',
      };
    }
  }
}

export default new PatientService();

